import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useSEOHead } from '../../hooks/useSEOHead';
import PageHero from '../../components/feature/PageHero';
import ProcessToggle from './components/ProcessToggle';
import KeyDifferentiators from './components/KeyDifferentiators';
import ComparisonTable from './components/ComparisonTable';
import WhichPathSection from './components/WhichPathSection';
import WhyRockhoundStrip from './components/WhyRockhoundStrip';
import HowItWorksFaq from './components/HowItWorksFaq';
import OwnerPhilosophyQuote from './components/OwnerPhilosophyQuote';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { injectFAQSchema, removeFAQSchema } from '../../lib/safeSchema';
import { pageFaqs } from '../../mocks/pageFaqs';

const BASE = 'https://rockhoundauto.com';

export default function HowItWorksPage() {
  useSEOHead({
    title: 'How It Works | Buy Online or Broker | Rockhound Auto',
    description: 'Two ways to buy: Browse our inventory online or use our concierge broker service. Transparent pricing, no pressure, delivery to your door. Better than Carvana.',
    canonicalUrl: 'https://rockhoundauto.com/how-it-works',
  });

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'How It Works',
          item: 'https://rockhoundauto.com/how-it-works',
        },
      ],
    };

    injectJsonLd('schema-how-it-works-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-how-it-works-breadcrumb');
    };
  }, []);

  useEffect(() => {
    // Inject FAQPage schema
    injectFAQSchema(
      'schema-hiw-faq',
      pageFaqs.howItWorks,
      'https://rockhoundauto.com/how-it-works#faqpage'
    );

    // Inject SpeakableSpecification schema
    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': 'https://rockhoundauto.com/how-it-works',
      name: 'How It Works - Rockhound Auto',
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: [
          '.hiw-step-title',
          '.hiw-step-feeling',
          '.hiw-step-description',
          '.hiw-faq-question',
          '.hiw-faq-answer'
        ]
      }
    };
    injectJsonLd('schema-hiw-speakable', speakableSchema);

    return () => {
      removeFAQSchema('schema-hiw-faq');
      removeJsonLd('schema-hiw-speakable');
    };
  }, []);

  return (
    <>
      <PageHero
        theme="gradient-warm"
        eyebrow={{
          icon: 'ri-lightbulb-line',
          text: 'Two Ways to Buy — You Choose'
        }}
        title={
          <>
            Smarter Than Carvana.
            <br />
            <span className="text-[#E8B84B]">Better Than Dealers.</span>
          </>
        }
        subtitle={
          <>
            Browse our inventory and buy online — or tell us exactly what you want and let our concierge broker find it anywhere in the country. Either way: transparent pricing, no pressure, and delivery to your door.
          </>
        }
        ctas={[
          { label: 'Browse Inventory', href: '/inventory', variant: 'primary', icon: 'ri-search-line' },
          { label: 'Use a Broker', href: '/broker-services', variant: 'secondary', icon: 'ri-user-star-line' }
        ]}
      >
        <p className="text-base text-white/75 mb-10 max-w-2xl mx-auto">
          No hidden fees. No &quot;let me check with my manager.&quot; No wondering if the person across town got a better deal. Just a car and a fair process — start to finish.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-14">
          {[
            { icon: 'ri-earth-line', title: 'Any Vehicle, Any State', sub: 'Not limited to one lot — we source from dealers, auctions, and private sellers nationwide.' },
            { icon: 'ri-truck-line', title: 'Delivered to Your Door', sub: 'Delivery often included. No dealership visit required. Ever.' },
            { icon: 'ri-price-tag-3-line', title: 'Transparent Pricing', sub: 'No hidden fees, no markups. Flat $995 broker fee when applicable. That\'s it.' },
          ].map((b) => (
            <div key={b.title} className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl px-5 py-5 text-center">
              <div className="w-9 h-9 flex items-center justify-center mx-auto mb-2">
                <i className={`${b.icon} text-[#E8B84B] text-2xl`}></i>
              </div>
              <div className="font-semibold text-white text-sm mb-1">{b.title}</div>
              <div className="text-white/70 text-xs leading-snug">{b.sub}</div>
            </div>
          ))}
        </div>
      </PageHero>

      <ProcessToggle />
      <KeyDifferentiators />
      <OwnerPhilosophyQuote />
      <ComparisonTable />
      <WhichPathSection />
      <WhyRockhoundStrip />
      <HowItWorksFaq />
    </>
  );
}